import { SaveOutlined } from '@ant-design/icons';
import { Col, Form, Input, InputNumber, message, Modal, Row, Spin } from 'antd';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import RouteInput from '@/components/FormItems/RouteInput';
import CountryInput from '@/components/FormItems/CountryInput';
import StationInput from '@/components/FormItems/StationInput';
import { useTranslation } from 'react-i18next';
import { clearAllSegmentsCache } from '@/hooks/data/masterDataHook';
import { apiSdk } from '@/httpClient';
import React, { useState } from 'react';

type Props = {
  onClose: (reload: boolean) => void;
};

export default function CreateSegmentModal({ onClose }: Props) {
  const [segmentForm] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const clearCache = () => {
    clearAllSegmentsCache();
  };
  const addSegment = values => {
    setLoading(true);
    apiSdk.BasicTariffService.createSegment({ body: values })
      .then(response => {
        if (response?.success) {
          onClose(true);
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const sourceCountryId = Form.useWatch('sourceCountryId', segmentForm);
  const destinationCountryId = Form.useWatch('destinationCountryId', segmentForm);
  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.small}
      className="nta-custom-form"
      title={t('baggageTariffsPage_CreateNewSegment')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={() => {
        segmentForm.resetFields();
        onClose(false);
      }}
      onOk={() => {
        segmentForm
          .validateFields()
          .then(values => {
            addSegment(values);
          })
          .catch(info => {});
      }}
    >
      <Spin spinning={loading}>
        <Form form={segmentForm} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>

          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item label={t('routesPage_OriginCountry')} name="sourceCountryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <CountryInput
                  onChange={() => {
                    segmentForm.setFieldValue('sourceId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('routesPage_OriginStation')} name="sourceId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <StationInput countryIds={sourceCountryId ? [sourceCountryId] : undefined} disabled={!sourceCountryId} />
              </Form.Item>

              <Form.Item
                label={t('routesPage_DestinationCountry')}
                name="destinationCountryId"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <CountryInput
                  onChange={() => {
                    segmentForm.setFieldValue('destinationId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('routesPage_DestinationStation')} name="destinationId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <StationInput countryIds={destinationCountryId ? [destinationCountryId] : undefined} disabled={!destinationCountryId} />
              </Form.Item>

              <Form.Item label={t('baggageTariffsPage_SelectRoute')} name="routeId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <RouteInput />
              </Form.Item>
              <Form.Item label={t('routesPage_Distance')} name="distance" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </Col>
            <Col md={12}></Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
